import styles from './FormCheckboxGroup.module.css';

/**
 * FormCheckboxGroup — vertically-stacked checkbox options.
 *
 * Multi-select: emits an array of selected values to onChange.
 * Checkbox size follows the canonical Henaket spec (40×40 standard,
 * 2px near-black border) since 0.36.362. This component adds the
 * SIZE VARIANT layer on top:
 *   size='standard' (default) → 40×40 checkbox, 16px gap, no row padding
 *   size='small'              → 24×24 checkbox, 8px gap, 8px vertical
 *                                row padding so the row still occupies
 *                                the standard 40px element height
 *
 * Props:
 *   value         — array of currently-selected option values
 *   onChange(arr) — called with the new array when user toggles one
 *   options       — Array<{value, label}>
 *   name          — base name (each checkbox gets name="${name}[]"
 *                   conceptually; in practice value lives in state)
 *   disabled      — disables the group (for canvas mock-up)
 *   ariaInvalid   — sets aria-invalid on each input
 *   size          — 'standard' | 'small' (default 'standard')
 *   selectAll     — 0.49.81: a label (e.g. 'Select all') adds one
 *                   tri-state checkbox above the options: ticked when
 *                   every option is ticked, a dash when some are,
 *                   empty when none. Clicking it ticks every option,
 *                   or unticks them all when all are ticked. Shown
 *                   only with two or more options.
 */
export default function FormCheckboxGroup({
  value = [],
  onChange,
  options = [],
  name,
  disabled = false,
  ariaInvalid = false,
  size = 'standard',
  // 0.44.01 - 'horizontal' puts the options on one line (wrapping when
  // the line is full), e.g. for filters above a table.
  layout = 'vertical',
  /* 0.48.39 - No top margin, as FormRadioGroup's flushTop: for a group
     on one line with other controls (a filter beside a search field),
     or right under a configuration label. */
  flushTop = false,
  selectAll = '',
}) {
  const safeValue = Array.isArray(value) ? value : [];
  const isSmall = size === 'small';
  const rowClass = [styles.row, isSmall && styles.rowSmall]
    .filter(Boolean).join(' ');
  const inputClass = [styles.input, isSmall && styles.inputSmall]
    .filter(Boolean).join(' ');

  const toggle = (optValue) => {
    const has = safeValue.includes(optValue);
    const next = has
      ? safeValue.filter(v => v !== optValue)
      : [...safeValue, optValue];
    onChange?.(next);
  };

  const allValues = options.map(o => o.value);
  const tickedCount = allValues.filter(v => safeValue.includes(v)).length;
  const allState = tickedCount === 0 ? 'none'
    : (tickedCount === allValues.length ? 'all' : 'some');
  const toggleAll = () => {
    if (allState === 'all') onChange?.(safeValue.filter(v => !allValues.includes(v)));
    else onChange?.([...safeValue.filter(v => !allValues.includes(v)), ...allValues]);
  };
  const allId = `${name || 'check'}-all`;

  return (
    <div className={[styles.group, layout === 'horizontal' && styles.groupHorizontal,
                     flushTop && styles.groupFlushTop].filter(Boolean).join(' ')}
         role="group">
      {selectAll && options.length > 1 && (
        <label className={rowClass} htmlFor={allId}>
          <input
            id={allId}
            className={inputClass}
            type="checkbox"
            checked={allState === 'all'}
            ref={(el) => { if (el) el.indeterminate = (allState === 'some'); }}
            aria-checked={allState === 'some' ? 'mixed' : (allState === 'all' ? 'true' : 'false')}
            disabled={disabled}
            onChange={toggleAll}
          />
          <span className={styles.label}>{selectAll}</span>
        </label>
      )}
      {options.map((opt, idx) => {
        const id = `${name || 'check'}-${idx}-${opt.value}`;
        const checked = safeValue.includes(opt.value);
        return (
          <label key={opt.value} className={rowClass} htmlFor={id}>
            <input
              id={id}
              className={inputClass}
              type="checkbox"
              name={name}
              value={opt.value}
              checked={checked}
              disabled={disabled}
              aria-invalid={ariaInvalid}
              onChange={() => toggle(opt.value)}
            />
            <span className={styles.label}>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
